<template>
  <div>
    <div v-if="formattedData.length >= 10000" class="card-panel red lighten-4"><h6>Too much data to plot</h6></div>
    <HighChart v-else :options="chartOptions"/>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'

export default {
  name: 'SectionChart',
  components: {
    HighChart: Chart
  },
  props: {
    railObject: {
      required: true
    },
    apiData: {
      required: true
    },
    dateFrom: {
      required: true
    },
    dateTo: {
      required: true
    },
    property: {
      required: true
    },
    section: {
      required: true
    },
    direction: {
      required: true
    }
  },
  data () {
    return {
      chartOptions: {
        chart: {
          type: 'line',
          zoomType: 'xy',
          zooming: {
            mouseWheel: {
              enabled: true
            }
          },
          events: {
            load () {
              const chart = this

              // Block scroll wheel unless Shift is held
              chart._wheelBlocker = (e) => {
                if (!e.shiftKey) {
                  if (e.stopImmediatePropagation) e.stopImmediatePropagation()
                  e.stopPropagation()
                }
              }
              chart.container.addEventListener('wheel', chart._wheelBlocker, { capture: true })

              // Show zoom cursor when Shift is held
              chart._cursorHint = (e) => {
                chart.container.style.cursor = e.shiftKey ? 'zoom-in' : ''
              }
              chart.container.addEventListener('mousemove', chart._cursorHint)

              // Flip cursor immediately on Shift press/release
              chart._onKeyShift = (e) => {
                if (e.key === 'Shift' && chart.container) {
                  chart.container.style.cursor = (e.type === 'keydown') ? 'zoom-in' : ''
                }
              }
              chart._offKeyDown = Highcharts.addEvent(document, 'keydown', chart._onKeyShift)
              chart._offKeyUp = Highcharts.addEvent(document, 'keyup', chart._onKeyShift)
            },
            destroy () {
              const el = this.container
              if (this._wheelBlocker && el) el.removeEventListener('wheel', this._wheelBlocker, { capture: true })
              if (this._cursorHint && el) el.removeEventListener('mousemove', this._cursorHint)
              if (this._offKeyDown) this._offKeyDown()
              if (this._offKeyUp) this._offKeyUp()
            }
          }
        },
        title: {
          text: 'Object Name'
        },
        credits: {
          enabled: false
        },
        accessibility: {
          enabled: false
        },
        subtitle: {
          text: 'property vs xAxis'
        },
        legend: {
          enabled: false
        },
        xAxis: {
          title: {
            text: ''
          },
          type: 'DateTime'
        },
        yAxis: {
          title: {
            text: 'property'
          }
        },
        plotOptions: {
          line: {
            states: {
              hover: {
                marker: {
                  enabled: false
                }
              }
            }
          }
        },
        tooltip: {
          enabled: true
        },
        series: [
          {
            name: 'datapoints',
            data: [],
            turboThreshold: 10000
          }
        ]
      },
      sections: [],
      properties: [],
      formattedData: [],
      formattedAPIData: []
    }
  },
  async mounted () {},
  methods: {
    formatApiData () {
      let apiData = []
      let properties = []
      let sections = []
      this.apiData.forEach(d => {
        let index = apiData.findIndex(x => x.id === d.Id)
        if (index === -1) {
          index = apiData.push({
            id: d.Id,
            dateTime: d.Dt,
            direction: d.Dir,
            sections: []
          }) - 1 // return new index
        }
        let sectionIndex = apiData[index].sections.findIndex(s => s.name === d.Section)
        if (sectionIndex === -1) {
          sectionIndex = apiData[index].sections.push({
            name: d.Section,
            start: d.SectionStart,
            end: d.SectionEnd,
            values: []
          }) - 1
        }
        apiData[index].sections[sectionIndex].values.push({
          property: d.PropName,
          value: d.Val
        })
        if (properties.findIndex(p => p.id === d.PropId) === -1) {
          properties.push({ id: d.PropId, name: d.PropName })
        }
        if (sections.indexOf(d.Section) === -1) {
          sections.push(d.Section)
        }
      })
      this.formattedAPIData = apiData
      this.properties = properties
      this.sections = sections

      this.$emit('updated', {properties, sections})
    },
    formatChartData () {
      if (this.formattedAPIData !== null) {
        const property = this.property
        const section = this.section
        const direction = this.direction
        let formattedData = []
        this.formattedAPIData.forEach(d => {
          if (d.direction === direction) {
            let sec = d.sections.find(s => s.name === section)
            if (sec !== undefined) {
              let result = sec.values.find(v => v.property === property)
              if (result !== undefined) {
                formattedData.push({
                  x: Date.parse(d.dateTime),
                  y: result.value
                })
              }
            }
          }
        })
        formattedData.sort((a, b) => (a.x - b.x))
        this.chartOptions.series[0].data = formattedData
      }
    },
    setChartData () {
      this.formatChartData()
      this.chartOptions.xAxis = {
        type: 'datetime',
        labels: {
          formatter () {
            return Highcharts.dateFormat('%d-%b-%y', (this.value))
          }
        },
        title: {
          text: this.getTranslationKey('Date')
        }
      }
      this.chartOptions.yAxis.title.text = this.getTranslationKey(this.property)
      this.chartOptions.title.text = this.railObject.ComplexName + ' ' + this.railObject.Name
      this.chartOptions.subtitle.text = this.getTranslationKey(this.property) + ' in section "' + this.section + '"'
    },
    getTranslationKey (val) {
      switch (val) {
        case 'Date':
          return this.$t('views.alarmlist.date')
        case 'Temperature':
          return this.$t('views.switch.temperature')
        case 'Engine':
          return this.$t('views.switch.engines')
        case 'Duration':
          return this.$t('views.switch.duration')
        case 'Alarm':
          return this.$t('alarm')
        case 'frequency':
          return this.$t('views.switch.frequency')
        default:
          return val
      }
    }
  },
  watch: {
    apiData (val) {
      if (val) {
        this.formatApiData()
        this.setChartData()
      }
    },
    direction (newValue) {
      this.formatChartData()
    },
    property (newValue) {
      if (this.properties.findIndex(p => p.name === newValue) !== -1) {
        this.setChartData()
      }
    },
    section (newValue) {
      if (this.sections.indexOf(newValue) !== -1) {
        this.setChartData()
      }
    }
  }
}
</script>

<style scoped>
  .title {
    font-family: Roboto;
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 23px !important;
    color: rgba(0, 0, 0, 0.87);
  }
</style>



// WEBPACK FOOTER //
// src/components/switch/analysis/SectionChart.vue